import { forwardRef, ChangeEvent, useMemo, useRef, useImperativeHandle, Context } from 'react'
import classnames from 'classnames'
import { AmplitudeContext, useInputTypingEventTracking, ContextType } from '@platform/utils'

import { AlertCircleIcon } from '../../assets/icons/icons'

import styles from './textarea.module.scss'

export type TextAreaBaseProps = {
  label: string
  hiddenLabel?: boolean
  helperText?: string
  optionalText?: string
  maxLength?: number
  id?: string
  rows?: number
  className?: string
  name?: string
  value?: string
  onChange: (event: ChangeEvent<HTMLTextAreaElement>) => void
  error?: string | React.ReactNode
  trackingIdentifier?: string
}

export type TextareaProps = TextAreaBaseProps &
  Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, keyof TextAreaBaseProps>

// TODO: This is a generic textarea, made based on the current task.
// Update if there is a component in figma
export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      maxLength,
      id,
      rows,
      className,
      label,
      hiddenLabel,
      helperText,
      optionalText,
      name,
      error = '',
      value,
      onChange,
      trackingIdentifier,
      ...rest
    },
    ref
  ) => {
    const innerRef = useRef<HTMLTextAreaElement | null>(null)
    useImperativeHandle(ref, () => innerRef.current as HTMLTextAreaElement)

    useInputTypingEventTracking(
      innerRef,
      AmplitudeContext as unknown as Context<ContextType>,
      'input_change',
      {
        id: trackingIdentifier,
        label,
      }
    )

    const renderErrorText = useMemo(
      () => (
        <div className={styles['error-wrapper']}>
          <AlertCircleIcon className={styles['error-icon']} />
          <p className={styles['error-text']}>{error}</p>
        </div>
      ),
      [error]
    )

    return (
      <div className={classnames(styles['textarea-wrapper'], className)}>
        <div className={styles['label-content']}>
          <label
            className={classnames(styles['label'], { [styles['visually-hidden']]: hiddenLabel })}
            htmlFor={id}
          >
            {label}
          </label>
          {optionalText && (
            <p className={classnames(styles['optional'], styles['optional-text'])}>
              {optionalText}
            </p>
          )}
        </div>
        {!!helperText && (
          <p id={`${id}helperText`} className={classnames(styles['helper-text'])}>
            {helperText}
          </p>
        )}
        <textarea
          name={name}
          className={classnames(styles['textarea'], { [styles['error']]: error })}
          maxLength={maxLength}
          id={id}
          rows={rows}
          ref={innerRef as React.Ref<HTMLTextAreaElement>}
          onChange={onChange}
          value={value}
          {...rest}
        />
        {maxLength && (
          <span className={styles['input-counter']}>
            {value?.length ?? 0}/{maxLength}
          </span>
        )}
        {error && renderErrorText}
      </div>
    )
  }
)

export default Textarea
